(ns machtfit.product-form.offer-layers
  (:require [re-frame.core :as rf]))

;;; ============================================
;;; DATA FETCHING
;;; ============================================

(rf/reg-event-db ::save-offer-layers-data
  (fn [db [_ data]]
    (assoc db :offer-layers-data data)))

(rf/reg-event-fx ::fetch-offer-layers-data
  (fn [{:keys [db]} _]
    {:post {:url "/api/"
            :csrf-token (get-in db [:io :csrf-token])
            :data '[(:offer-layers-data {} :offer-layers)]
            :on-success #(rf/dispatch [::save-offer-layers-data (-> % :result :offer-layers :result)])}}))

;;; ============================================
;;; SUBSCRIPTIONS
;;; ============================================

(rf/reg-sub ::delivery-types
  :<- [:get [:offer-layers-data :delivery-types]]
  (fn [types _] types))

(rf/reg-sub ::filtered-service-formats
  :<- [:get [:offer-layers-data :service-formats]]
  :<- [:get [:product :what :offer-layers :delivery-type]]
  (fn [[all-formats delivery-type-id] _]
    (if delivery-type-id
      (filter #(= (:delivery-type-id %) delivery-type-id) all-formats)
      [])))

(rf/reg-sub ::filtered-channels
  :<- [:get [:offer-layers-data :channels]]
  :<- [:get [:product :what :offer-layers :delivery-type]]
  (fn [[all-channels delivery-type-id] _]
    (if delivery-type-id
      (filter #(some #{delivery-type-id} (:delivery-type-ids %)) all-channels)
      [])))

(rf/reg-sub ::access-formats
  :<- [:get [:offer-layers-data :access-formats]]
  (fn [formats _] formats))

(rf/reg-sub ::health-domains
  :<- [:get [:offer-layers-data :health-domains]]
  (fn [domains _] domains))

(rf/reg-sub ::health-categories
  :<- [:get [:offer-layers-data :health-categories]]
  (fn [categories _] categories))

(rf/reg-sub ::health-categories-grouped
  :<- [::health-categories]
  :<- [::health-domains]
  (fn [[categories domains] _]
    ; Return vector of [domain {domain_info, categories_in_domain}]
    ; Preserves domain order
    (for [domain domains]
      [(:id domain)
       {:domain domain
        :categories (filter #(some #{(:id domain)} (:health-domain-ids %))
                            categories)}])))

;;; ============================================
;;; EVENTS (STATE UPDATES)
;;; ============================================

(rf/reg-event-db ::toggle-enabled
  (fn [db [_]]
    (update-in db [:product :what :offer-layers :enabled?] not)))

(rf/reg-event-db ::set-delivery-type
  (fn [db [_ type-id]]
    (-> db
        (assoc-in [:product :what :offer-layers :delivery-type] type-id)
        ; Reset layers 2-5 when layer 1 changes
        (assoc-in [:product :what :offer-layers :service-format] nil)
        (assoc-in [:product :what :offer-layers :channels] [])
        (assoc-in [:product :what :offer-layers :access-format] nil)
        (assoc-in [:product :what :offer-layers :health-categories] []))))

(rf/reg-event-db ::set-service-format
  (fn [db [_ format-id]]
    (assoc-in db [:product :what :offer-layers :service-format] format-id)))

(rf/reg-event-db ::toggle-channel
  (fn [db [_ channel-id]]
    (update-in db [:product :what :offer-layers :channels]
               (fn [channels]
                 (if (some #{channel-id} channels)
                   (vec (remove #{channel-id} channels))
                   (conj channels channel-id))))))

(rf/reg-event-db ::set-access-format
  (fn [db [_ format-id]]
    (assoc-in db [:product :what :offer-layers :access-format] format-id)))

(rf/reg-event-db ::toggle-health-category
  (fn [db [_ cat-id]]
    (let [current (get-in db [:product :what :offer-layers :health-categories])]
      (if (some #{cat-id} current)
        ; Remove if already selected
        (update-in db [:product :what :offer-layers :health-categories]
                   #(vec (remove #{cat-id} %)))
        ; Add if less than 3 selected
        (if (< (count current) 3)
          (update-in db [:product :what :offer-layers :health-categories]
                     #(conj % cat-id))
          db))))) ; Silently ignore if max reached

;;; ============================================
;;; LAYER COMPONENTS
;;; ============================================

(defn- layer-1-delivery-type []
  (let [selected @(rf/subscribe [:get [:product :what :offer-layers :delivery-type]])
        delivery-types @(rf/subscribe [::delivery-types])]
    [:div.offer-layer-section
     [:h4.offer-layer-title "Schritt 1: Angebotstyp"]
     [:p.offer-layer-description
      "Wählen Sie die grundlegende Form, in der Ihr Angebot geliefert wird:"]
     [:div.offer-layer-tiles
      (for [dt delivery-types]
        ^{:key (:id dt)}
        [:div.offer-layer-tile
         {:class (when (= selected (:id dt)) "selected")
          :on-click #(rf/dispatch [::set-delivery-type (:id dt)])}
         [:div.tile-title (:title-de dt)]
         [:div.tile-description (:description-de dt)]])]]))

(defn- layer-2-service-format []
  (let [selected @(rf/subscribe [:get [:product :what :offer-layers :service-format]])
        service-formats @(rf/subscribe [::filtered-service-formats])]
    [:div.offer-layer-section
     [:h4.offer-layer-title "Schritt 2: Leistungsformat"]
     [:p.offer-layer-description
      "Beschreiben Sie konkret, wie das Angebot konsumiert wird:"]
     [:div.offer-layer-tiles
      (for [sf service-formats]
        ^{:key (:id sf)}
        [:div.offer-layer-tile
         {:class (when (= selected (:id sf)) "selected")
          :on-click #(rf/dispatch [::set-service-format (:id sf)])}
         [:div.tile-title (:title-de sf)]
         [:div.tile-description (:description-de sf)]])]]))

(defn- layer-3-channels []
  (let [selected @(rf/subscribe [:get [:product :what :offer-layers :channels]])
        channels @(rf/subscribe [::filtered-channels])]
    [:div.offer-layer-section
     [:h4.offer-layer-title "Schritt 3: Kanäle / Nutzungsumfeld"]
     [:p.offer-layer-description
      "Wählen Sie ein oder mehrere Kanäle, durch die das Angebot genutzt wird:"]
     [:div.channels-list
      (for [channel channels]
        ^{:key (:id channel)}
        [:div.channel-item
         {:class (when (some #{(:id channel)} selected) "selected")
          :on-click #(rf/dispatch [::toggle-channel (:id channel)])}
         [:input.filled-in
          {:id (str "channel-" (:id channel))
           :type "checkbox"
           :checked (boolean (some #{(:id channel)} selected))
           :read-only true}]
         [:div.channel-content
          [:div.channel-title (:title-de channel)]
          [:div.channel-description (:description-de channel)]]])]]))

(defn- layer-4-access-format []
  (let [selected @(rf/subscribe [:get [:product :what :offer-layers :access-format]])
        access-formats @(rf/subscribe [::access-formats])]
    [:div.offer-layer-section
     [:h4.offer-layer-title "Schritt 4: Zugangsformat"]
     [:p.offer-layer-description
      "Wie wird der Zugang gewährt und die Nutzung abgerechnet?"]
     [:div.offer-layer-tiles
      (for [af access-formats]
        ^{:key (:id af)}
        [:div.offer-layer-tile
         {:class (when (= selected (:id af)) "selected")
          :on-click #(rf/dispatch [::set-access-format (:id af)])}
         [:div.tile-title (:title-de af)]
         [:div.tile-description (:description-de af)]])]]))

(defn- layer-5-health-categories []
  (let [selected @(rf/subscribe [:get [:product :what :offer-layers :health-categories]])
        grouped @(rf/subscribe [::health-categories-grouped])]
    [:div.offer-layer-section
     [:h4.offer-layer-title "Schritt 5: Gesundheitskategorien"]
     [:p.offer-layer-description
      "Wählen Sie 1–3 Gesundheitskategorien, die Ihr Angebot am besten beschreiben:"]
     [:div.health-categories-container
      (for [[domain-id {:keys [domain categories]}] grouped]
        ^{:key domain-id}
        [:div.health-domain {:data-domain-id domain-id}
         [:h5.domain-title (:title-de domain)]
         [:p.domain-description (:description-de domain)]
         [:div.categories-in-domain
          (for [category categories]
            ^{:key (:id category)}
            [:div.category-item
             {:class (when (some #{(:id category)} selected) "selected")
              :on-click #(rf/dispatch [::toggle-health-category (:id category)])}
             [:input.filled-in
              {:id (str "cat-" (:id category))
               :type "checkbox"
               :checked (boolean (some #{(:id category)} selected))
               :disabled (and (= (count selected) 3)
                              (not (some #{(:id category)} selected)))
               :read-only true}]
             [:div.category-content
              [:div.category-title (:title-de category)]
              [:div.category-description (:description-de category)]]])]])]]))

;;; ============================================
;;; MAIN CONTAINER COMPONENT
;;; ============================================

(defn section-offer-layers []
  (let [enabled? @(rf/subscribe [:get [:product :what :offer-layers :enabled?]])
        delivery-type @(rf/subscribe [:get [:product :what :offer-layers :delivery-type]])]
    (when enabled?
      [:div.offer-layers-container
       [layer-1-delivery-type]
       (when delivery-type
         [:<>
          [layer-2-service-format]
          [layer-3-channels]
          [layer-4-access-format]
          [layer-5-health-categories]])])))
